import React from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Loader2, type LucideIcon } from 'lucide-react'
import { useStoreTheme } from '@/lib/useStoreTheme'

export interface PageHeaderProps {
  title?: string
  subtitle?: string
  badge?: string | number
  badgeColor?: 'brand' | 'emerald' | 'amber' | 'rose' | 'slate'
  action?: React.ReactNode
  backTo?: string
  onBack?: () => void
  backLabel?: string
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  badge,
  badgeColor = 'brand',
  action,
  backTo,
  onBack,
  backLabel,
  className = '',
}: PageHeaderProps) {
  const theme = useStoreTheme()

  const lightBadgeClasses: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-700 border-brand-200/80',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
    amber: 'bg-amber-50 text-amber-700 border-amber-200/80',
    rose: 'bg-rose-50 text-rose-700 border-rose-200/80',
    slate: 'bg-slate-100 text-slate-700 border-slate-200/80',
  }

  const darkBadgeClasses: Record<string, string> = {
    brand: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    slate: 'bg-slate-800 text-slate-300 border-slate-700',
  }

  const badgeMap = theme.isDarkTheme ? darkBadgeClasses : lightBadgeClasses

  const hasArrowInLabel = typeof backLabel === 'string' && (backLabel.startsWith('←') || backLabel.startsWith('<-'))
  const label = backLabel || 'Назад'

  return (
    <div className={`px-4 pt-4 pb-3 ${className}`}>
      {(backTo || onBack) && (
        <div className={title ? 'mb-2' : ''}>
          {backTo ? (
            <Link
              to={backTo}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                theme.isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {!hasArrowInLabel && <ArrowLeft className="h-3.5 w-3.5" />}
              {label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={onBack}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                theme.isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {!hasArrowInLabel && <ArrowLeft className="h-3.5 w-3.5" />}
              {label}
            </button>
          )}
        </div>
      )}
      {title && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={`text-xl font-bold tracking-tight truncate ${theme.isDarkTheme ? 'text-white' : 'text-slate-900'}`}>{title}</h1>
            {badge !== undefined && (
              <span
                className={`px-2.5 py-0.5 text-xs font-bold rounded-full border shadow-2xs shrink-0 ${
                  badgeMap[badgeColor] || badgeMap.brand
                }`}
              >
                {badge}
              </span>
            )}
          </div>
          {action && <div className="shrink-0 flex items-center gap-2">{action}</div>}
        </div>
      )}
      {subtitle && <p className={`mt-1 text-xs leading-relaxed ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}>{subtitle}</p>}
    </div>
  )
}

export interface StorefrontCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  interactive?: boolean
  highlight?: boolean
}

export function StorefrontCard({
  children,
  className = '',
  interactive = false,
  highlight = false,
  ...props
}: StorefrontCardProps) {
  const theme = useStoreTheme()

  const cardBaseStyle = theme.isDarkTheme
    ? `rounded-2xl border ${theme.bgCardClass} ${theme.borderClass} ${theme.textPrimaryClass} shadow-md transition-all ${
        highlight ? 'border-amber-500/60 ring-1 ring-amber-500/20' : 'hover:border-amber-500/30'
      }`
    : `rounded-2xl border bg-white shadow-xs transition-all ${
        highlight ? 'border-brand-300 ring-1 ring-brand-100' : 'border-slate-200/80 hover:border-slate-300'
      }`

  return (
    <div
      className={`${cardBaseStyle} ${interactive ? 'cursor-pointer hover:shadow-md active:scale-[0.99]' : ''} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

export interface StorefrontEmptyStateProps {
  icon: LucideIcon
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
    disabled?: boolean
  }
  className?: string
}

export function StorefrontEmptyState({
  icon: Icon,
  title,
  description,
  action,
  className = '',
}: StorefrontEmptyStateProps) {
  const theme = useStoreTheme()

  return (
    <div
      className={`flex flex-col items-center justify-center p-12 text-center ${
        theme.isDarkTheme ? 'text-slate-400' : 'text-slate-400'
      } ${className}`}
    >
      <div
        className={`mb-3.5 flex h-14 w-14 items-center justify-center rounded-2xl shadow-2xs ${
          theme.isDarkTheme
            ? 'bg-[#1f2732] text-amber-400 ring-1 ring-amber-500/20'
            : 'bg-slate-100 text-slate-400 ring-1 ring-slate-200/60'
        }`}
      >
        <Icon className="h-7 w-7 opacity-85" />
      </div>
      <p className={`text-sm font-semibold ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-700'}`}>{title}</p>
      {description && (
        <p className={`mt-1 max-w-xs text-xs leading-relaxed ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-400'}`}>{description}</p>
      )}
      {action && (
        <button
          type="button"
          disabled={action.disabled}
          onClick={action.onClick}
          className={`mt-4 rounded-xl active:scale-95 px-4 py-2 text-xs font-semibold shadow-xs transition disabled:opacity-50 ${
            theme.isDarkTheme ? theme.accentBtnClass : 'bg-brand-600 hover:bg-brand-700 text-white'
          }`}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

export function StorefrontLoading({ message = 'Завантаження...' }: { message?: string }) {
  const theme = useStoreTheme()
  return (
    <div className={`flex flex-col items-center justify-center p-12 gap-2 ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-400'}`}>
      <Loader2 className={`h-6 w-6 animate-spin ${theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'}`} />
      <p className={`text-xs font-medium ${theme.isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>{message}</p>
    </div>
  )
}

export interface StorefrontPageProps {
  children: React.ReactNode
  header?: React.ReactNode
  title?: string
  subtitle?: string
  badge?: string | number
  action?: React.ReactNode
  backTo?: string
  onBack?: () => void
  backLabel?: string
  className?: string
  contentClassName?: string
}

export function StorefrontPage({
  children,
  header,
  title,
  subtitle,
  badge,
  action,
  backTo,
  onBack,
  backLabel,
  className = '',
  contentClassName = '',
}: StorefrontPageProps) {
  const theme = useStoreTheme()
  const showHeader = Boolean(header || title || backTo || onBack)

  return (
    <div className={`min-h-screen pb-24 transition-colors ${theme.bgPageClass} ${theme.textPrimaryClass} ${className}`}>
      {header || (showHeader ? (
        <PageHeader
          title={title}
          subtitle={subtitle}
          badge={badge}
          action={action}
          backTo={backTo}
          onBack={onBack}
          backLabel={backLabel}
        />
      ) : null)}
      <div className={`px-4 pb-4 ${contentClassName}`}>{children}</div>
    </div>
  )
}
